import React, { useState } from 'react';
import { SearchIcon, ChevronRightIcon, CarIcon, ParkingSquareIcon, TruckIcon, BusIcon } from 'lucide-react';
interface Service {
  id: string;
  title: string;
  description: string;
  category: string;
  estimatedTime: string;
  fee: string;
}
const services: Service[] = [
// Parking Services
{
  id: 'ps1',
  title: 'Residential Parking Permit',
  description: 'Apply for or renew residential street parking permits',
  category: 'Parking Services',
  estimatedTime: '3-5 business days',
  fee: '$75.00 annually'
}, {
  id: 'ps2',
  title: 'Visitor Parking Permit',
  description: 'Request temporary parking permits for visitors',
  category: 'Parking Services',
  estimatedTime: '1-2 business days',
  fee: '$25.00 per month'
}, {
  id: 'ps3',
  title: 'Special Event Parking Permit',
  description: 'Obtain parking permits for special events or occasions',
  category: 'Parking Services',
  estimatedTime: '5-7 business days',
  fee: 'Varies by event'
},
// Commercial Vehicles
{
  id: 'cv1',
  title: 'Commercial Vehicle Route Permit',
  description: 'Apply for permits to operate commercial vehicles on local routes',
  category: 'Commercial Vehicles',
  estimatedTime: '7-10 business days',
  fee: '$200.00 annually'
}, {
  id: 'cv2',
  title: 'Loading Zone Permit',
  description: 'Request access to commercial loading zones',
  category: 'Commercial Vehicles',
  estimatedTime: '5-7 business days',
  fee: '$150.00 annually'
}, {
  id: 'cv3',
  title: 'Oversize Vehicle Permit',
  description: 'Obtain permits for oversized commercial vehicles',
  category: 'Commercial Vehicles',
  estimatedTime: '7-10 business days',
  fee: '$250.00 per permit'
},
// Transit Services
{
  id: 'ts1',
  title: 'Transit Pass Application',
  description: 'Apply for monthly or annual transit passes',
  category: 'Transit Services',
  estimatedTime: '2-3 business days',
  fee: 'Varies by type'
}, {
  id: 'ts2',
  title: 'Reduced Fare Program',
  description: 'Apply for senior, student, or low-income transit fare programs',
  category: 'Transit Services',
  estimatedTime: '5-7 business days',
  fee: 'No fee'
}, {
  id: 'ts3',
  title: 'Paratransit Service Registration',
  description: 'Register for accessible transportation services',
  category: 'Transit Services',
  estimatedTime: '7-10 business days',
  fee: 'No fee'
}];
const CategoryIcon = ({
  category
}: {
  category: string;
}) => {
  switch (category) {
    case 'Parking Services':
      return <ParkingSquareIcon className="h-6 w-6 text-blue-600" data-id="element-5412" />;
    case 'Commercial Vehicles':
      return <TruckIcon className="h-6 w-6 text-green-600" data-id="element-5413" />;
    case 'Transit Services':
      return <BusIcon className="h-6 w-6 text-purple-600" data-id="element-5414" />;
    default:
      return <CarIcon className="h-6 w-6 text-gray-600" data-id="element-5415" />;
  }
};
const ServiceCard = ({
  service
}: {
  service: Service;
}) => {
  return <div className="bg-white rounded-lg border border-gray-200 hover:border-blue-500 hover:shadow-sm transition-all duration-200" data-id="element-5416">
      <div className="p-5 flex flex-col h-full" data-id="element-5417">
        <div className="flex-1" data-id="element-5418">
          <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-5419">
            {service.title}
          </h3>
          <p className="text-sm text-gray-600 mb-6 line-clamp-2" data-id="element-5420">
            {service.description}
          </p>
        </div>
        <div className="space-y-4" data-id="element-5421">
          <div className="flex items-center space-x-4 text-sm text-gray-500 pt-4 border-t border-gray-100" data-id="element-5422">
            <div className="flex items-center" data-id="element-5423">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-5424">
                ⏱
              </span>
              <span data-id="element-5425">{service.estimatedTime}</span>
            </div>
            <div className="flex items-center" data-id="element-5426">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-5427">
                💰
              </span>
              <span data-id="element-5428">{service.fee}</span>
            </div>
          </div>
          <button className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-150" data-id="element-5429">
            Apply Now
            <span className="ml-1" data-id="element-5430">→</span>
          </button>
        </div>
      </div>
    </div>;
};
const CategorySection = ({
  category,
  services
}: {
  category: string;
  services: Service[];
}) => {
  return <div className="mb-8" data-id="element-5431">
      <div className="flex items-center mb-4" data-id="element-5432">
        <CategoryIcon category={category} data-id="element-5433" />
        <h2 className="text-xl font-semibold text-gray-900 ml-2" data-id="element-5434">{category}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-5435">
        {services.map(service => <ServiceCard key={service.id} service={service} data-id="element-5436" />)}
      </div>
    </div>;
};
export const TransportationPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredServices = services.filter(service => service.title.toLowerCase().includes(searchQuery.toLowerCase()) || service.description.toLowerCase().includes(searchQuery.toLowerCase()));
  const categories = Array.from(new Set(filteredServices.map(service => service.category)));
  return <div className="min-h-screen bg-gray-50" data-id="element-5437">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-5438">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-gray-500 mb-8" data-id="element-5439">
          <a href="/" className="hover:text-gray-700" data-id="element-5440">
            Dashboard
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-5441" />
          <span className="text-gray-900" data-id="element-5442">Transportation Services</span>
        </div>
        {/* Header */}
        <div className="mb-8" data-id="element-5443">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-5444">
            Transportation Services
          </h1>
          <p className="text-gray-600" data-id="element-5445">
            Manage parking permits, commercial vehicle routes, and transit
            services
          </p>
        </div>
        {/* Search */}
        <div className="mb-8" data-id="element-5446">
          <div className="max-w-md relative" data-id="element-5447">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-5448">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-5449" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Search services..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-5450" />
          </div>
        </div>
        {/* Service Categories */}
        {categories.map(category => <CategorySection key={category} category={category} services={filteredServices.filter(service => service.category === category)} data-id="element-5451" />)}
      </div>
    </div>;
};